// The admin code menu (spec 2026-10-09), opened by "Go to code menu" under
// Find player. VIEW lists every code and what it gives; ADD is the same list
// with + on top and Edit / Delete / Copy on each row. Admin-only: the button
// is built only for admins, and every RPC re-checks caller_is_admin().
import type { SupabaseClient } from '@supabase/supabase-js';
import { el } from './dom';
import { adminDeleteCode, adminListCodes, adminSaveCode, type AdminCode } from './codes';
import {
  CODE_NAME_MAX,
  CODE_REWARD_BOXES,
  isValidCodeName,
  parseRewardLine,
  rewardLabel,
  rewardToLine,
  type CodeReward,
} from '../config/redeemCodes';

type Tab = 'view' | 'add';

const BOX_EXAMPLES = [
  'x300 coins',
  'tower ninja',
  'x5 item goldstone',
  'x2 potion chili',
  'x3 bags',
];

export function openCodeMenu(client: SupabaseClient): void {
  document.querySelector('[data-testid="codes-admin"]')?.remove();

  const backdrop = el('div', { className: 'codes-admin-backdrop', testid: 'codes-admin' });
  // The game listens on the window: keys typed here must not drive it.
  backdrop.addEventListener('keydown', (e) => e.stopPropagation());
  backdrop.addEventListener('keyup', (e) => e.stopPropagation());

  const close = el('button', { className: 'codes-admin-close', testid: 'codes-admin-close' }, '✕');
  close.addEventListener('click', () => backdrop.remove());
  const viewTab = el('button', { className: 'codes-tab', testid: 'codes-tab-view' }, 'View');
  const addTab = el('button', { className: 'codes-tab', testid: 'codes-tab-add' }, 'Add');
  const body = el('div', { className: 'codes-admin-body', testid: 'codes-admin-body' });
  const status = el('div', { className: 'account-error', testid: 'codes-admin-error' });

  const panel = el(
    'div',
    { className: 'codes-admin' },
    el(
      'div',
      { className: 'codes-admin-head' },
      el('h2', {}, 'Code menu'),
      el('div', { className: 'codes-tabs' }, viewTab, addTab),
      close,
    ),
    status,
    body,
  );
  backdrop.append(panel);
  (document.getElementById('account-root') ?? document.body).append(backdrop);

  let tab: Tab = 'view';
  let codes: AdminCode[] = [];
  let loaded = false;
  /** True while the list is on screen (not the name step or the editor). */
  let onList = true;

  viewTab.addEventListener('click', () => {
    tab = 'view';
    renderList();
  });
  addTab.addEventListener('click', () => {
    tab = 'add';
    renderList();
  });

  async function refresh(): Promise<void> {
    const r = await adminListCodes(client);
    if (!r.ok) {
      status.textContent = r.error;
      return;
    }
    status.textContent = '';
    codes = r.value;
    loaded = true;
  }

  function markTabs(): void {
    viewTab.classList.toggle('active', tab === 'view');
    addTab.classList.toggle('active', tab === 'add');
  }

  // ── the list (both tabs) ─────────────────────────────────────────────────
  function renderList(): void {
    onList = true;
    markTabs();
    const rows: HTMLElement[] = [];
    if (tab === 'add') {
      const plus = el('button', { className: 'codes-plus', testid: 'codes-plus' }, '+');
      plus.title = 'Add a new code';
      plus.addEventListener('click', renderNameStep);
      rows.push(plus);
    }
    if (!loaded) rows.push(el('div', { className: 'codes-empty' }, 'Loading…'));
    else if (codes.length === 0)
      rows.push(el('div', { className: 'codes-empty' }, 'No codes yet.'));
    for (const c of codes) rows.push(codeRow(c));
    body.replaceChildren(
      el('div', { className: 'codes-list', testid: `codes-list-${tab}` }, ...rows),
    );
  }

  function codeRow(c: AdminCode): HTMLElement {
    const gives = el(
      'div',
      { className: 'codes-gives' },
      ...c.rewards.map((r) => el('span', { className: 'codes-chip' }, rewardLabel(r))),
    );
    const row = el(
      'div',
      { className: 'codes-row', testid: `codes-row-${c.code}` },
      el('div', { className: 'codes-name' }, c.code),
      gives,
    );
    if (tab !== 'add') return row;

    const edit = el('button', { className: 'codes-small', testid: `codes-edit-${c.code}` }, 'Edit');
    edit.addEventListener('click', () => renderEditor(c.code, c.rewards, false));

    // Two presses, no browser dialog: the first arms it for three seconds.
    const del = el(
      'button',
      { className: 'codes-small codes-del', testid: `codes-delete-${c.code}` },
      'Delete',
    );
    let armed: ReturnType<typeof setTimeout> | undefined;
    del.addEventListener('click', () => {
      if (!armed) {
        del.textContent = 'Sure?';
        armed = setTimeout(() => {
          armed = undefined;
          del.textContent = 'Delete';
        }, 3000);
        return;
      }
      clearTimeout(armed);
      del.disabled = true;
      void adminDeleteCode(client, c.code).then(async (r) => {
        if (!r.ok) {
          status.textContent = r.error;
          del.disabled = false;
          return;
        }
        await refresh();
        renderList();
      });
    });

    const copy = el('button', { className: 'codes-small', testid: `codes-copy-${c.code}` }, 'Copy');
    copy.addEventListener('click', () => {
      void navigator.clipboard?.writeText(c.code).then(
        () => flash(copy, 'Copied'),
        () => flash(copy, 'Failed'),
      );
    });

    row.append(el('div', { className: 'codes-actions' }, edit, del, copy));
    return row;
  }

  // ── step 1: name the new code ────────────────────────────────────────────
  function renderNameStep(): void {
    onList = false;
    const input = el('input', {
      type: 'text',
      maxLength: CODE_NAME_MAX,
      placeholder: 'Code name',
      testid: 'codes-name-input',
    });
    const error = el('div', { className: 'account-error', testid: 'codes-name-error' });
    const cancel = el(
      'button',
      { className: 'account-secondary', testid: 'codes-name-cancel' },
      'Cancel',
    );
    const confirm = el(
      'button',
      { className: 'codes-confirm', testid: 'codes-name-confirm' },
      'Confirm',
    );
    cancel.addEventListener('click', renderList);
    const go = () => {
      const name = input.value.trim();
      if (!isValidCodeName(name)) {
        error.textContent = `Use 1–${CODE_NAME_MAX} letters, numbers, - or _ (no spaces).`;
        return;
      }
      if (codes.some((c) => c.code === name)) {
        error.textContent = 'A code with that name already exists.';
        return;
      }
      renderEditor(name, [], true);
    };
    confirm.addEventListener('click', go);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') go();
    });
    body.replaceChildren(
      el(
        'div',
        { className: 'codes-step' },
        el('h3', {}, 'Name the new code'),
        el('div', { className: 'account-hint' }, 'Players type this exactly — capitals matter.'),
        input,
        error,
        el('div', { className: 'codes-buttons' }, cancel, confirm),
      ),
    );
    input.focus();
  }

  // ── step 2: the five boxes (new code, or editing one) ────────────────────
  function renderEditor(name: string, current: CodeReward[], isNew: boolean): void {
    onList = false;
    const boxes: { input: HTMLInputElement; error: HTMLDivElement }[] = [];
    const grid = el('div', { className: 'codes-boxes' });
    for (let i = 0; i < CODE_REWARD_BOXES; i++) {
      const input = el('input', {
        type: 'text',
        maxLength: 60,
        placeholder: `e.g. ${BOX_EXAMPLES[i]}`,
        value: current[i] ? rewardToLine(current[i]) : '',
        testid: `codes-box-${i}`,
      });
      const error = el('div', { className: 'codes-box-error', testid: `codes-box-error-${i}` });
      input.addEventListener('input', () => (error.textContent = ''));
      boxes.push({ input, error });
      grid.append(el('div', { className: 'codes-box' }, input, error));
    }
    const formError = el('div', { className: 'account-error', testid: 'codes-editor-error' });
    const cancel = el(
      'button',
      { className: 'account-secondary', testid: 'codes-editor-cancel' },
      'Cancel',
    );
    const check = el('button', { className: 'codes-check', testid: 'codes-editor-check' }, '✓');
    check.title = isNew ? 'Add this code' : 'Save this code';
    // Cancel never adds a new code, and never removes an existing one.
    cancel.addEventListener('click', renderList);
    check.addEventListener('click', () => {
      formError.textContent = '';
      const rewards: CodeReward[] = [];
      let bad = false;
      for (const b of boxes) {
        const r = parseRewardLine(b.input.value);
        if (r.ok === 'empty') {
          b.error.textContent = '';
        } else if (r.ok) {
          b.error.textContent = '';
          rewards.push(r.reward);
        } else {
          b.error.textContent = r.error;
          bad = true;
        }
      }
      if (bad) return;
      if (rewards.length === 0) {
        formError.textContent = 'Fill in at least one box.';
        return;
      }
      check.disabled = true;
      void adminSaveCode(client, name, rewards, isNew).then(async (r) => {
        check.disabled = false;
        if (!r.ok) {
          formError.textContent = r.error;
          return;
        }
        await refresh();
        renderList();
      });
    });
    body.replaceChildren(
      el(
        'div',
        { className: 'codes-step' },
        el('h3', { testid: 'codes-editor-title' }, `${isNew ? 'New code' : 'Edit code'}: ${name}`),
        el(
          'div',
          { className: 'account-hint' },
          'Write xN then what to give: coins, darkcoins, lightcoins, capycoins, bags, goldenbags, ' +
            'capsules, debris, item <name>, potion <name>, tower <name>, icon <name>. ' +
            'Towers and icons need no amount. Leave a box empty to skip it.',
        ),
        grid,
        formError,
        el('div', { className: 'codes-buttons' }, cancel, check),
      ),
    );
    boxes[0].input.focus();
  }

  markTabs();
  renderList();
  // The first load can land after the admin has already pressed + (a slow
  // network): it may only redraw the list, never throw away an open editor.
  void refresh().then(() => {
    if (onList) renderList();
  });
}

function flash(button: HTMLButtonElement, text: string): void {
  const was = button.textContent;
  button.textContent = text;
  setTimeout(() => (button.textContent = was), 1200);
}
